<script setup lang="ts">
    // TODO(refactor): migrate to `useScene` composable like every other viewport.
    // This component currently bypasses useScene because:
    //   1. The matching OverlaySideView owns the scene context for each
    //      catstack plane (set via overlayStore.setSceneContextForView), and
    //      this component mounts before any SideView, so it can't synchronously
    //      read its scene context — handled by the setTimeout(100) retry below.
    //   2. The catstack-mode watcher rebuilds the runtime in place, which
    //      doesn't fit useScene's mount-once-per-component-instance semantic.
    // Proper fix: make sceneContexts reactive in overlayStore (ref<Map<...>>),
    // wait for the matching context via a watcher here, and add
    // :key="overlayStore.catstackMode" on this component in OverlayViewer.vue
    // so Vue handles the mode-change remount naturally. See the "Known exception"
    // section in docs/threejs-practices.md for full context.
    import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
    import { OrthographicCamera } from 'three';
    import { stop, stopAll, type StopHandle } from '@/util';
    import { useAppErrorStore } from '@/stores/appErrorStore';
    import { useOverlayStore } from '@/views/dicoms/overlay/overlayStore';
    import { createSceneRuntime } from '@/lib/threeScene/runtime/createSceneRuntime';
    import { attachRenderingState } from '@/lib/threeScene/scene/attachRenderingState';
    import { renderingState } from '@/lib/threeScene/renderingState';

    const appError = useAppErrorStore();
    const overlayStore = useOverlayStore();

    const containerRef = ref<HTMLDivElement>();
    const canvasRef = ref<HTMLCanvasElement>();

    const nodes = computed(() => overlayStore.getNodesForView(overlayStore.catstackMode));

    let updates: StopHandle | null = null;

    function cleanupMainView() {
        if (updates) {
            stop(updates);
            updates = null;
        }
    }

    function initialiseMainView() {
        const [canvas, container] = [canvasRef.value, containerRef.value];
        if (!canvas || !container) {
            throw Error('DOM elements are not defined');
        }

        cleanupMainView();
        const sceneContext = overlayStore.getSceneContextForView(overlayStore.catstackMode);

        // The matching SideView for this mode populates the scene context
        // asynchronously; retry until it's ready.
        if (!sceneContext) {
            setTimeout(() => initialiseMainView(), 100);
            return;
        }

        const camera = new OrthographicCamera();
        const runtime = createSceneRuntime({
            container,
            canvas,
            scene: sceneContext.scene,
            camera,
            resizeDebounceMs: 10,
        });

        // Shared CameraState (position/zoom synced across SideView and
        // MainView via the overlay store); per-view RenderingState wraps it.
        const state = renderingState({ camera: nodes.value.camera });
        const stopRendering = attachRenderingState({
            runtime,
            state,
            scene: sceneContext.scene,
            camera,
            container,
            canvas,
        });

        updates = stopAll(stopRendering, runtime.stop);
    }

    watch(() => overlayStore.catstackMode, () => {
        initialiseMainView();
    });

    onMounted(() => {
        initialiseMainView();
    });

    onUnmounted(() => {
        appError.catchErrors(() => {
            stop(updates);
        });
    });
</script>

<template>
    <div ref="containerRef" class="main-view">
        <canvas ref="canvasRef" class="main-canvas" />
        <div class="view-label">
            {{ overlayStore.catstackMode.toUpperCase() }}
        </div>
    </div>
</template>

<style scoped lang="scss">
    .main-view {
        width: 600px;
        height: 600px;
        position: relative;
        border-radius: 4px;
        overflow: hidden;
        background-color: rgb(var(--v-theme-scene-background));
    }

    .main-canvas {
        position: absolute;
        width: 100%;
        height: 100%;
    }

    .view-label {
        position: absolute;
        top: 8px;
        left: 8px;
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 6px 12px;
        border-radius: 4px;
        font-size: 14px;
        font-weight: bold;
        pointer-events: none;
        z-index: 10;
    }
</style>
